<script>
  import { onMount, onDestroy } from 'svelte';
  import { getAuthHeaders } from '../utils/apiClient';
  
  let activeUsers = 0;
  let history = []; // {time: string, count: number, timestamp?: string}
  let interval;
  let maxDataPoints = 288; // 24시간 (5분 간격)
  let isLoading = true;

  // 서버에서 과거 히스토리 데이터를 가져오기
  async function fetchHistory() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/active_users_history?max_points=' + maxDataPoints, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.history && data.history.length > 0) {
          history = data.history.map(d => ({
            time: d.time,
            count: d.count,
            timestamp: d.timestamp
          }));
          activeUsers = history[history.length - 1].count;
        }
      }
    } catch (e) {
      console.error("History fetch error:", e);
    }
    isLoading = false;
  }

  // 현재 동접자 수만 가져와서 히스토리에 추가
  async function fetchActiveUsers() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/active_users_count', { headers });
      if (response.ok) {
        const data = await response.json();
        activeUsers = data.active_users;
        
        const now = new Date();
        const timeStr = now.getHours() + ":" + now.getMinutes().toString().padStart(2, '0');
        
        history = [...history, { time: timeStr, count: activeUsers, timestamp: now.toISOString() }];
        if (history.length > maxDataPoints) {
          history = history.slice(-maxDataPoints);
        }
      }
    } catch (e) {
      console.error("Fetch error:", e);
    }
  }

  onMount(async () => {
    // 1. 먼저 과거 데이터를 서버에서 로드 (주식 차트처럼 과거 이력을 즉시 표시)
    await fetchHistory();
    
    // 2. 이후 5분마다 실시간 갱신 (서버의 백그라운드 기록과 동기화)
    await fetchActiveUsers();
    interval = setInterval(fetchActiveUsers, 300000);
  });

  onDestroy(() => {
    if (interval) clearInterval(interval);
  });

  // 차트에 표시할 데이터 (너무 많으면 간추리기)
  $: displayHistory = history.length > 60 
    ? history.filter((_, i) => i % Math.ceil(history.length / 60) === 0 || i === history.length - 1)
    : history;
  $: maxCount = displayHistory.length > 0 ? Math.max(...displayHistory.map(d => d.count), 5) : 5;
  $: displayMaxPoints = displayHistory.length || 1;

  // Y축 레이블 계산
  $: yLabels = (() => {
    const labels = [];
    const step = Math.ceil(maxCount / 4);
    for (let i = 0; i <= 4; i++) {
      labels.push(i * step);
    }
    return labels;
  })();
</script>

<div class="active-users-container">
  <h1>실시간 사용자 접속 현황</h1>
  
  <div class="status-card">
    <div class="live-indicator">
        <span class="pulse"></span> LIVE
    </div>
    <div class="count-hero">
        <span class="number">{activeUsers}</span>
        <span class="unit">명의 사용자가 접속 중</span>
    </div>

    {#if isLoading}
      <div class="chart-loading">
        <div class="spinner"></div>
        <p>과거 접속 데이터 로딩 중...</p>
      </div>
    {:else}
    <!-- 커스텀 SVG 차트 (주식 차트 스타일) -->
    <div class="chart-container">
        <div class="chart-y-axis">
          {#each yLabels.slice().reverse() as label}
            <span>{label}</span>
          {/each}
        </div>
        <div class="chart-area">
          <svg viewBox="0 0 400 150" preserveAspectRatio="none">
              <!-- 격자선 -->
              {#each Array(5) as _, i}
                  <line x1="0" y1={150 - (i * 37.5)} x2="400" y2={150 - (i * 37.5)} stroke="#eee" stroke-width="0.5" />
              {/each}

              <!-- 데이터 선 -->
              {#if displayHistory.length > 1}
                  <polyline
                      fill="none"
                      stroke="#4A90E2"
                      stroke-width="2.5"
                      stroke-linejoin="round"
                      stroke-linecap="round"
                      points={displayHistory.map((d, i) => `${(i / (displayMaxPoints - 1)) * 400},${150 - (d.count / maxCount) * 140}`).join(' ')}
                  />
                  
                  <!-- 영역 채우기 -->
                  <polygon
                      fill="url(#gradient)"
                      opacity="0.3"
                      points={`0,150 ${displayHistory.map((d, i) => `${(i / (displayMaxPoints - 1)) * 400},${150 - (d.count / maxCount) * 140}`).join(' ')} 400,150`}
                  />

                  <!-- 현재 위치 점 -->
                  {#if displayHistory.length > 0}
                    {@const lastIdx = displayHistory.length - 1}
                    {@const lastX = (lastIdx / (displayMaxPoints - 1)) * 400}
                    {@const lastY = 150 - (displayHistory[lastIdx].count / maxCount) * 140}
                    <circle cx={lastX} cy={lastY} r="4" fill="#4A90E2" />
                    <circle cx={lastX} cy={lastY} r="7" fill="none" stroke="#4A90E2" stroke-width="1.5" opacity="0.5" />
                  {/if}
              {/if}

              <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style="stop-color:#4A90E2;stop-opacity:1" />
                      <stop offset="100%" style="stop-color:#4A90E2;stop-opacity:0" />
                  </linearGradient>
              </defs>
          </svg>
        </div>
        
        <div class="time-labels">
            {#if displayHistory.length > 0}
                <span>{displayHistory[0].time}</span>
                {#if displayHistory.length > 2}
                  <span>{displayHistory[Math.floor(displayHistory.length / 2)].time}</span>
                {/if}
                <span>{displayHistory[displayHistory.length - 1].time}</span>
            {/if}
        </div>
    </div>
    {/if}
  </div>

  <div class="stats-row">
    <div class="stat-card">
      <span class="stat-label">데이터 포인트</span>
      <span class="stat-value">{history.length}개</span>
    </div>
    <div class="stat-card">
      <span class="stat-label">최대 동접</span>
      <span class="stat-value">{history.length > 0 ? Math.max(...history.map(d => d.count)) : 0}명</span>
    </div>
    <div class="stat-card">
      <span class="stat-label">갱신 주기</span>
      <span class="stat-value">5분</span>
    </div>
  </div>

  <div class="info-section">
    <h3>차트 데이터 안내</h3>
    <p>서버가 5분마다 자동으로 동접자 수를 기록합니다. 페이지에 접속하면 과거 이력(최대 24시간)을 포함한 그래프가 즉시 표시되며, 이후 5분마다 실시간으로 갱신됩니다.</p>
  </div>
</div>

<style>
  .active-users-container {
    max-width: 900px;
    margin: 40px auto;
    padding: 20px;
  }

  h1 {
    font-family: 'HakgyoansimDunggeunmisoTTF-B', sans-serif;
    text-align: center;
    margin-bottom: 40px;
    color: #333;
  }

  .status-card {
    background: white;
    border-radius: 24px;
    padding: 30px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.08);
    position: relative;
    overflow: hidden;
  }

  .live-indicator {
      position: absolute;
      top: 20px;
      right: 20px;
      background: #fdf2f2;
      color: #e02424;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 6px;
  }

  .count-hero {
      text-align: center;
      margin-bottom: 30px;
  }

  .number {
      font-size: 64px;
      font-weight: 800;
      color: #4A90E2;
      display: block;
  }

  .unit {
      color: #666;
      font-size: 18px;
  }

  .chart-container {
      width: 100%;
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      position: relative;
  }

  .chart-area {
      height: 200px;
      width: 100%;
  }

  .chart-y-axis {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      font-size: 11px;
      color: #999;
      padding-right: 8px;
      height: 200px;
      position: absolute;
      left: 0;
  }

  svg {
      width: 100%;
      height: 100%;
  }

  .time-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
      color: #999;
      font-size: 12px;
  }

  .pulse {
    width: 8px;
    height: 8px;
    background-color: #e02424;
    border-radius: 50%;
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% { transform: scale(0.95); opacity: 1; }
    70% { transform: scale(1.5); opacity: 0; }
    100% { transform: scale(0.95); opacity: 0; }
  }

  .chart-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0;
    color: #999;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #eee;
    border-top: 3px solid #4A90E2;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .stats-row {
    display: flex;
    gap: 16px;
    margin-top: 20px;
  }

  .stat-card {
    flex: 1;
    background: white;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    text-align: center;
  }

  .stat-label {
    display: block;
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }

  .stat-value {
    display: block;
    font-size: 20px;
    font-weight: 700;
    color: #333;
  }

  .info-section {
    margin-top: 20px;
    background: #f8fafc;
    padding: 20px;
    border-radius: 16px;
    color: #475569;
  }

  @media (max-width: 600px) {
    .stats-row {
      flex-direction: column;
      gap: 8px;
    }
  }
</style>
